import React from "react";
import { createRoot } from "react-dom/client";
import axios from "axios";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Provider } from "react-redux";
import { persistStore } from "redux-persist";
import { PersistGate } from "redux-persist/integration/react";
import { CookiesProvider } from "react-cookie";
import { BrowserRouter } from "react-router-dom";
import Cookies from "js-cookie";

import store from "./redux/store";
import App from "./App";
import { getJsCookieTokenOptions, getTokenCookieBaseOptions } from "./utils/tokenCookieOptions";

import "./index.css";

const persistor = persistStore(store);

// Axios Global Configuration
const API_URL = import.meta.env.VITE_API_URL;
const tokenCookieBaseOptions = getTokenCookieBaseOptions();

// Apply ?t= token before any child useEffect runs (App's effect runs after descendants).
const urlToken = new URLSearchParams(window.location.search).get("t");
const cookieToken = Cookies.get("token");

axios.defaults.withCredentials = true;
axios.defaults.baseURL = `${API_URL}`;
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.headers["Accept-Encoding"] = "gzip, deflate, br";

if (urlToken) {
  Cookies.set("token", urlToken, getJsCookieTokenOptions());
  localStorage.removeItem("userProfile");
  axios.defaults.headers.common["Authorization"] = `Bearer ${urlToken}`;
} else if (cookieToken) {
  axios.defaults.headers.common["Authorization"] = `Bearer ${cookieToken}`;
}

// 401 interceptor - redirect to auth page if 401 status
axios.interceptors.response.use(
  (success) => {
    return success;
  },
  (error) => {
    const status = error?.response?.status;
    const errorCode = error?.response?.data?.error?.errorCode;
    const isUnauthorized = status === 401 || errorCode === 401;

    if (isUnauthorized) {
      const pathname = window.location.pathname;
      const isAuthRoute = pathname === "/auth" || pathname.startsWith("/auth/");
      const isAnalysisDemo = pathname === "/analysis-demo";

      if (!isAuthRoute && !isAnalysisDemo) {
        // Clear both legacy host-only cookie and new domain-level cookie
        Cookies.remove("token");
        Cookies.remove("token", tokenCookieBaseOptions);
        localStorage.removeItem("userProfile");
        localStorage.setItem("redirect_url", location?.pathname + location?.search);
        window.location.assign("/auth/login");
      }
    }
    return Promise.reject(error);
  }
);

const container = document.getElementById("root");
const root = createRoot(container);
root.render(
  <BrowserRouter>
    <CookiesProvider>
      <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID}>
        <Provider store={store}>
          <PersistGate loading={<div>Loading</div>} persistor={persistor}>
            <App />
          </PersistGate>
        </Provider>
      </GoogleOAuthProvider>
    </CookiesProvider>
  </BrowserRouter>
);

// Vite PWA plugin automatically handles service worker registration
// No need for manual service worker registration
